import {
  CircleHelp,
  FileText,
  Globe,
  Laptop,
  MessageSquare,
  Monitor,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import {
  legalNoticePath,
  legalNotices,
  type LegalNoticeId,
} from "@/components/legal/legalNotices";
import {
  headerButtonClassName,
  headerMenuEnterStyle,
  headerStackedToolClassName,
  headerToolClassName,
} from "@/components/layout/headerButtonClassName";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useHeaderMenuFocus } from "@/hooks/useHeaderMenuFocus";

interface HelpSupportLink {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  showsPdfBadge?: boolean;
}

interface HelpHeaderButtonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const helpSupportLinks: HelpSupportLink[] = [
  {
    label: "Hard- und Software Support",
    description:
      "Benötigst Du Unterstützung mit deinem System bei dir vor Ort? (Hardware, installierte Software, Office 365 Account)",
    href: "https://lernplattform.gfn.de/local/support/formularneu.php?type=hardware",
    icon: Monitor,
  },
  {
    label: "Lernplattform Support",
    description:
      "Benötigst Du Unterstützung mit der Lernplattform? (z.B. Funktionalität der Webseite)",
    href: "https://lernplattform.gfn.de/local/support/formularneu.php?type=lernplattform",
    icon: Globe,
  },
  {
    label: "RemoteLab Support",
    description:
      "Benötigst Du Unterstützung mit deinem RemoteLab? (z.B. Verbindungsprobleme)",
    href: "https://lernplattform.gfn.de/local/support/formularneu.php?type=remotelab",
    icon: Laptop,
  },
  {
    label: "Begleitendes Coaching",
    description:
      "Prüfungsangst, gesundheitliche Probleme, Trennung, Lernschwierigkeiten, kein Praktikumsplatz…",
    href: "https://lernplattform.gfn.de/local/support/lib/pdf/GFN-Begleitendes-Coaching_DIN%20A4_2024-10_TN%201.pdf",
    icon: MessageSquare,
    showsPdfBadge: true,
  },
];

const legalLinks: { id: LegalNoticeId; icon: LucideIcon }[] = [
  { id: "datenschutz", icon: ShieldCheck },
  { id: "impressum", icon: FileText },
];

const helpSupportIconClassName =
  "flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary-bright/25 text-[color-mix(in_oklab,var(--secondary)_40%,var(--secondary-bright))]";

const renderPdfBadge = (link: HelpSupportLink) => {
  if (!link.showsPdfBadge) {
    return null;
  }

  return (
    <Badge variant="outline" className="border-secondary-bright text-secondary">
      PDF
    </Badge>
  );
};

const HelpSupportLinkRow = ({ link }: { link: HelpSupportLink }) => {
  const Icon = link.icon;

  return (
    <li>
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-start gap-3 rounded-md p-2 outline-none hover:bg-secondary-bright/15 focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span aria-hidden="true" className={helpSupportIconClassName}>
          <Icon className="size-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2 font-bold text-primary">
            {link.label}
            {renderPdfBadge(link)}
          </span>
          <span className="text-sm text-muted-foreground">
            {link.description}
          </span>
        </span>
        <span className="sr-only">öffnet in neuem Tab</span>
      </a>
    </li>
  );
};

const LegalNoticeLink = ({
  label,
  icon: Icon,
  to,
  onSelect,
}: {
  label: string;
  icon: LucideIcon;
  to: string;
  onSelect: () => void;
}) => (
  <Link
    to={to}
    className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 text-left font-semibold text-primary outline-none hover:bg-secondary-bright/15 focus-visible:ring-[3px] focus-visible:ring-ring/50"
    onClick={onSelect}
  >
    <Icon aria-hidden="true" className="size-4 text-secondary" />
    {label}
  </Link>
);

/**
 * Hilfe control in the app header — support links and legal notice pages.
 */
const HelpHeaderButton = ({ open, onOpenChange }: HelpHeaderButtonProps) => {
  const { enrollmentId, courseExternalId } = useParams<{
    enrollmentId?: string;
    courseExternalId?: string;
  }>();
  const { notePointerOpen, noteKeyboardOpen, keepTriggerFocused } =
    useHeaderMenuFocus();

  const closeHelpMenu = () => {
    onOpenChange(false);
  };

  const hasCourseScope = Boolean(enrollmentId || courseExternalId);
  const visibleLegalLinks = legalLinks.filter((link) => {
    if (link.id === "datenschutz") return hasCourseScope;
    return true;
  });

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Hilfe und rechtliche Hinweise"
          className={[
            "border-transparent text-primary-foreground hover:cursor-pointer",
            headerButtonClassName,
            headerStackedToolClassName,
            headerToolClassName(open),
          ].join(" ")}
          onPointerDown={notePointerOpen}
          onKeyDown={noteKeyboardOpen}
        >
          <CircleHelp className="size-4" />
          Hilfe
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        aria-label="Hilfe und rechtliche Hinweise"
        className="w-[440px] max-w-[calc(100vw-2rem)] rounded-xl p-2 data-[state=closed]:pointer-events-none"
        style={headerMenuEnterStyle}
        onOpenAutoFocus={keepTriggerFocused}
        onCloseAutoFocus={keepTriggerFocused}
      >
        <ul className="m-0 list-none p-0">
          {helpSupportLinks.map((link) => (
            <HelpSupportLinkRow key={link.href} link={link} />
          ))}
        </ul>
        <div className="mx-2 my-2 h-px bg-border" role="separator" />
        <p className="px-3 py-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Rechtliches
        </p>
        <div className="grid grid-cols-2 gap-2 px-2 pb-2">
          {visibleLegalLinks.map((link) => (
            <LegalNoticeLink
              key={link.id}
              label={legalNotices[link.id].title}
              icon={link.icon}
              to={legalNoticePath(link.id, { enrollmentId, courseExternalId })}
              onSelect={closeHelpMenu}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { HelpHeaderButton };
